import { Column, FilterMultiSelectorProps, usePicklistData } from '@access-erp/common';
import {
  AdvancedFilterCustomRenderProps,
  AdvancedFilterValue,
  AutocompleteValue,
  CellType,
  DynamicDataMultiselect,
  Option,
} from '@access-erp/ui-components';

import { GetCustomersLookup } from '@/graphql/contracts/_generated_/getCustomersLookUp.query';
import { useGetCustomersAutocompleteListQuery } from '../hooks/useGetCustomersAutocompleteListQuery';
import GetCustomesLookUpQuery from '@/graphql/contracts/getCustomersLookUp.query.gql';
import { Forms } from '@/graphql/contracts/_generated_/contracts';

const defaultColumns: Column<any>[] = [
  {
    name: 'customerAccountNumber',
    caption: 'Account Code',
    width: 300,
    cellType: CellType.String,
  },
  { name: 'customerName', caption: 'Account Name', width: 300, cellType: CellType.String },
];

const getFilterValueByName = (values: AdvancedFilterValue[], name: string): string[] =>
  values?.find((x) => x.name === name)?.value as string[];


const CustomerAccountCodeMultiSelector = ({
  title,
  value,
  onFilterValueChanged,
  name,
  disabled,
}: FilterMultiSelectorProps<any[]>): React.JSX.Element => {
  const modalProps = usePicklistData({
    headerTitle: title,
    columns: defaultColumns as Column<Option<AutocompleteValue> & { id: string | number }>[],
    configurationName: Forms.CustomersLookup,
    queryDocument: GetCustomesLookUpQuery,
    dataMapper: (data: GetCustomersLookup) => {
      const dataWithValueAndLabel = data?.customerGrid?.lists?.customersLookup?.items?.map(
        (item) => ({
          ...item,
          value: item?.customerAccountNumber ?? '',
          label: item?.customerAccountNumber ?? '',
          id: item?.customerAccountNumber ?? '',
        }),
      );
      return {
        items: dataWithValueAndLabel,
        pageInfo: data.customerGrid?.lists?.customersLookup?.pageInfo!,
      };
    },
  });

  return (
    <DynamicDataMultiselect
      key={`${title}-selector`}
      autocompleteProps={{
        disabled,
        dataTestId: name,
        filterValueChanged: onFilterValueChanged,
        dataFetching: {
          dataMapper: (data: GetCustomersLookup) => {
            return (
              data?.customerGrid?.lists?.customersLookup?.items?.map((item) => ({
                id: item?.customerAccountNumber ?? '',
                label: item?.customerAccountNumber ?? '',
                value: item?.customerAccountNumber ?? '',
              })) ?? []
            );
          },
          useGetData: (first, search) => {
            const { data, loading } = useGetCustomersAutocompleteListQuery({
              variables: {
                filter: {
                  summary: search,
                },
                first: first,
              },
            });
            return {
              data: data as GetCustomersLookup,
              loading,
            };
          },
        },
        value: value?.map((x) => ({ id: x, label: x, value: x })) ?? [],
      }}
      modalProps={modalProps}
    />
  );
};

export default CustomerAccountCodeMultiSelector;

export const renderCustomerAccountCode = ({
  name,
  filters,
  onFilterValueChanged,
  title,
  value,
}: AdvancedFilterCustomRenderProps<string[]>): React.JSX.Element => (
  <CustomerAccountCodeMultiSelector
    name={name}
    title={title}
    key="customerAccountNumber"
    value={value || getFilterValueByName(filters, name)}
    onFilterValueChanged={onFilterValueChanged}
  />
);
